/**
 * TanStack Query hooks for the M365 licence-consumption board.
 *
 * Same shape as the hooks in `src/lib/api/hooks.ts` — one hook per endpoint,
 * wrapping the generated SDK function, with a stable `queryKey`.
 *
 * Licence assignment moves on a human timescale and the backend already caches
 * its Graph snapshot for fifteen minutes, so these poll far more gently than
 * the monitoring hooks. Refetching harder would only re-serve the same cached
 * snapshot.
 */

import { useQuery } from "@tanstack/react-query";

import {
  getLicenseOverview,
  getLicenseSkus,
  getLicenseWaste,
  getReclaimableAccounts,
} from "@/lib/api/generated";
import { logger } from "@/lib/logger";

/** Shared prefix so a future invalidation can drop the whole board at once. */
const LICENSES_KEY = ["licenses"] as const;

/** Backend snapshot TTL is 15 min; re-asking sooner just re-serves the cache. */
const REFETCH_INTERVAL_MS = 5 * 60_000;

export function useLicenseOverview() {
  return useQuery({
    queryKey: [...LICENSES_KEY, "overview"],
    queryFn: async () => {
      const { data } = await getLicenseOverview();
      logger.debug("[licenses] overview received", {
        configured: data?.configured,
        countedSkus: data?.totals.counted_skus,
        namesConcealed: data?.names_concealed,
      });
      return data;
    },
    refetchInterval: REFETCH_INTERVAL_MS,
  });
}

export function useLicenseSkus() {
  return useQuery({
    queryKey: [...LICENSES_KEY, "skus"],
    queryFn: async () => {
      const { data } = await getLicenseSkus();
      logger.debug("[licenses] sku list received", { skus: data?.skus.length });
      return data;
    },
    refetchInterval: REFETCH_INTERVAL_MS,
  });
}

export function useLicenseWaste() {
  return useQuery({
    queryKey: [...LICENSES_KEY, "waste"],
    queryFn: async () => {
      const { data } = await getLicenseWaste();
      logger.debug("[licenses] waste view received", { rows: data?.rows.length });
      return data;
    },
    refetchInterval: REFETCH_INTERVAL_MS,
  });
}

/**
 * The identity drill-down. The backend decides what comes back: an admin
 * session gets `accounts`, everyone else gets the same aggregate counts with
 * `admin: false` and an empty list. The client never gates this itself —
 * a role check in the browser is a UI affordance, not access control.
 */
export function useReclaimableAccounts() {
  return useQuery({
    queryKey: [...LICENSES_KEY, "reclaimable-accounts"],
    queryFn: async () => {
      const { data } = await getReclaimableAccounts();
      logger.debug("[licenses] reclaimable accounts received", {
        admin: data?.admin,
        accounts: data?.accounts.length,
      });
      return data;
    },
    // Personal data — keep it out of the cache for a fraction of the time the
    // aggregate panels are held.
    staleTime: 60_000,
    gcTime: 2 * 60_000,
  });
}
